import React from 'react';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

// icons
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import TodayIcon from '@mui/icons-material/Today';

// ----------------------------------------------------------------------

const LegendContainer = styled(Paper)(({theme}) => ({
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: theme.shape.borderRadius,
  border: `1px dotted ${theme.palette.divider}`,
}));

const LegendItemStyle = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  fontSize: '0.75rem',
}));

const ColorDotStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'colorType',
})(({theme, colorType}) => {
  // Map colorType to actual background color
  let backgroundColor;

  switch (colorType) {
    case 'success':
      backgroundColor = alpha(theme.palette.success.main, 0.12);
      break;
    case 'error':
      backgroundColor = alpha(theme.palette.error.main, 0.08);
      break;
    case 'warning':
      backgroundColor = alpha(theme.palette.warning.main, 0.08);
      break;
    case 'primary':
      backgroundColor = theme.palette.primary.main;
      break;
    default:
      backgroundColor = 'transparent';
  }

  return {
    width: 12,
    height: 12,
    marginRight: theme.spacing(0.5),
    borderRadius: '50%',
    backgroundColor,
    flexShrink: 0,
  };
});

// ----------------------------------------------------------------------

export function AvailabilityLegend() {
  return (
    <LegendContainer elevation={0}>
      <Typography variant="caption" sx={{fontWeight: 'bold', mb: 1, display: 'block'}}>
        Légende
      </Typography>

      <Grid container spacing={0.5}>
        <Grid item xs={6}>
          <LegendItemStyle>
            <ColorDotStyle colorType="success" />
            <EventAvailableIcon sx={{fontSize: 12, color: 'success.main', mr: 0.5}} />
            <Typography variant="caption">Disponible</Typography>
          </LegendItemStyle>
        </Grid>

        <Grid item xs={6}>
          <LegendItemStyle>
            <ColorDotStyle colorType="error" />
            <EventBusyIcon sx={{fontSize: 12, color: 'error.main', mr: 0.5}} />
            <Typography variant="caption">Indisponible</Typography>
          </LegendItemStyle>
        </Grid>

        <Grid item xs={6}>
          <LegendItemStyle>
            <Box sx={{
              width: 12,
              height: 12,
              mr: 0.5,
              borderRadius: '50%',
              border: (theme) => `2px dashed ${theme.palette.warning.main}`,
              backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
            }} />
            <Typography variant="caption">Horaire modifié</Typography>
          </LegendItemStyle>
        </Grid>

        <Grid item xs={6}>
          <LegendItemStyle>
            <Box sx={{
              width: 12,
              height: 12,
              mr: 0.5,
              borderRadius: '50%',
              border: (theme) => `2px dashed ${theme.palette.error.main}`,
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
            }} />
            <Typography variant="caption">Fermé</Typography>
          </LegendItemStyle>
        </Grid>

        <Grid item xs={6}>
          <LegendItemStyle>
            <Box sx={{
              width: 12,
              height: 12,
              mr: 0.5,
              borderRadius: '50%',
              border: (theme) => `1px dashed ${theme.palette.primary.main}`,
            }} />
            <TodayIcon sx={{fontSize: 12, color: 'primary.main', mr: 0.5}} />
            <Typography variant="caption">Aujourd&apos;hui</Typography>
          </LegendItemStyle>
        </Grid>

        <Grid item xs={6}>
          <LegendItemStyle>
            <Box sx={{
              width: 12,
              height: 12,
              mr: 0.5,
              borderRadius: '50%',
              backgroundColor: (theme) => theme.palette.primary.main,
            }} />
            <Typography variant="caption" sx={{color: 'primary.main'}}>Sélectionnée</Typography>
          </LegendItemStyle>
        </Grid>
      </Grid>
    </LegendContainer>
  );
}
